import React, { useEffect, useState } from "react";
import axios from "axios";
import { backend_url } from "../App";
import "./CSS/varietyAccessories.css";
import { menuItems } from "../Constants/menuItems";
import { isAdmin } from "../utils/roleUtils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const LandingBanner = () => {
  const [landingBanner, setLandingBanner] = useState([]);
  const [formData, setFormData] = useState({
    category: "",
    routeTo: "",
    image: null,
    title: "",
    subtitle: "",
    badgeText: "",
    ctaText: "",
    discountText: "",
    secondaryImage: null,
  });
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewSecondaryImage, setPreviewSecondaryImage] = useState(null);
  const [existingSecondaryImage, setExistingSecondaryImage] = useState("");

  const handleDelete = (_id) => {
    setSelectedVideoId(_id);
    setShowConfirm(true);
  };

  // Handle delete
  const confirmDelete = async (id) => {
    try {
      await axios.delete(`${backend_url}/api/landingbanner/${id}`);
      setShowConfirm(false);
      toast.success("Video Deleted Successfully");
      fetchLandingBanners(); //Refresh after deletion
    } catch (error) {
      console.error("Error deleting video:", error);
    }
  };

  // Fetch all varieties
  useEffect(() => {
    fetchLandingBanners();
  }, []);

  const fetchLandingBanners = async () => {
    try {
      const response = await axios.get(`${backend_url}/api/landingbanner`);
      setLandingBanner(response.data);
    } catch (error) {
      console.error("Error fetching landingbanners:", error);
    }
  };

  // Handle form input change
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      const file = files[0];
      setFormData({ ...formData, image: file });
      if (file) setPreviewImage(URL.createObjectURL(file));
    } else if (name === "secondaryImage") {
      const file = files[0];
      setFormData({ ...formData, secondaryImage: file });
      if (file) setPreviewSecondaryImage(URL.createObjectURL(file));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // Handle category change and filter route options
  const handleCategoryChange = (e) => {
    const selected = e.target.value;
    setSelectedCategory(selected);
    setFormData({ ...formData, category: selected });

    if (selected) {
      const selectedCategory = menuItems.find(
        (item) => item.value === selected
      );

      if (selectedCategory && selectedCategory.dropdown) {
        // Flatten dropdown and subcategories
        const options = selectedCategory.dropdown.flatMap((item) => {
          if (item.value === null) {
            return [
              { label: `-- ${item.label} --`, value: null },
              ...(item.subcategories || []),
            ];
          } else {
            return item;
          }
        });

        setCategoryOptions(options);
      } else {
        setCategoryOptions([]);
      }
    } else {
      setCategoryOptions([]);
    }
  };

  // Add or update variety
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!editingId && formData.image && formData.image.size > 3 * 1024 * 1024) {
      toast.error("Image size should not exceed 1 MB");
      return;
    }

    const data = new FormData();
    data.append("routeTo", formData.routeTo);
    data.append("image", formData.image);
    data.append("category", formData.category);
    data.append("title", formData.title);
    data.append("subtitle", formData.subtitle);
    data.append("badgeText", formData.badgeText);
    data.append("ctaText", formData.ctaText);
    data.append("discountText", formData.discountText);
    if (formData.secondaryImage) {
      data.append("secondaryImage", formData.secondaryImage);
    } else {
      data.append("secondaryImage", existingSecondaryImage);
    }

    try {
      if (editingId) {
        await axios.put(`${backend_url}/api/landingbanner/${editingId}`, data);
        toast.success("Banner Updated Successfully");
      } else {
        await axios.post(`${backend_url}/api/landingbanner`, data);
        toast.success("Banner Added Successfully");
      }
      fetchLandingBanners();
      closeModal();
    } catch (error) {
      console.error("Error saving landing Banners:", error);
    }
  };

  // Handle edit
  const handleEdit = (landingbanner) => {
    setFormData({
      routeTo: landingbanner.routeTo,
      image: null, // don't assign image URL to file field
      category: landingbanner.category,
      title: landingbanner.title || "",
      subtitle: landingbanner.subtitle || "",
      badgeText: landingbanner.badgeText || "",
      ctaText: landingbanner.ctaText || "",
      discountText: landingbanner.discountText || "",
      secondaryImage: null, // don't assign image URL to file field
    });
    setPreviewImage(landingbanner.image); // <-- show existing image
    setPreviewSecondaryImage(landingbanner.secondaryImage || null);
    setExistingSecondaryImage(landingbanner.secondaryImage || "");

    setSelectedCategory(landingbanner.category);

    // Generate correct dropdown
    const selectedCategory = menuItems.find(
      (item) => item.value === landingbanner.category
    );
    if (selectedCategory && selectedCategory.dropdown) {
      const options = selectedCategory.dropdown.flatMap((item) => {
        if (item.value === null) {
          return [
            { label: `-- ${item.label} --`, value: null },
            ...(item.subcategories || []),
          ];
        } else {
          return item;
        }
      });
      setCategoryOptions(options);
    } else {
      setCategoryOptions([]);
    }

    setEditingId(landingbanner._id);
    setShowModal(true);
  };

  // Close modal and reset form
  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({ routeTo: "", image: null, category: "", title: "", subtitle: "", badgeText: "", ctaText: "", discountText: "", secondaryImage: null });
    setSelectedCategory("");
    setCategoryOptions([]);
    setPreviewImage(null);
    setPreviewSecondaryImage(null);
    setExistingSecondaryImage("");
  };

  const formatText = (text) =>
    text.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  return (
    <>
      <div className="variety-admin">
        <div className="headerSections">
          <div className="head-title">Homepage Banners</div>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            + Add Banners
          </button>
        </div>

        {/* Popup Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{editingId ? "Edit Banner" : "Add Banner"}</h2>
              <form onSubmit={handleSubmit} className="variety-form">
                <div className="form-group">
                  <label htmlFor="category">Category</label>
                  <select
                    id="category"
                    name="category"
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    required
                  >
                    <option value="">Select a category</option>
                    {menuItems.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Route To Dropdown */}
                <div className="form-group">
                  <label htmlFor="routeTo">Route To</label>
                  <select
                    id="routeTo"
                    name="routeTo"
                    value={formData.routeTo}
                    onChange={handleChange}
                    required
                    disabled={!selectedCategory}
                  >
                    <option value="">Select a route</option>
                    {categoryOptions.map((option) =>
                      option.value ? (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ) : (
                        <option key={option.label} value="" disabled>
                          {option.label}
                        </option>
                      )
                    )}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="title">Title</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g. Sale On Live"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subtitle">Subtitle</label>
                  <input
                    type="text"
                    id="subtitle"
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleChange}
                    placeholder="e.g. Premium sarees & fabrics"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="badgeText">Badge Text</label>
                  <input
                    type="text"
                    id="badgeText"
                    name="badgeText"
                    value={formData.badgeText}
                    onChange={handleChange}
                    placeholder="e.g. 10% OFF Everything"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ctaText">Button Text</label>
                  <input
                    type="text"
                    id="ctaText"
                    name="ctaText"
                    value={formData.ctaText}
                    onChange={handleChange}
                    placeholder="e.g. Shop Now"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="image">Image</label>

                  {/* Hide original file input */}
                  <input
                    type="file"
                    id="image"
                    name="image"
                    onChange={handleChange}
                    accept="image/*"
                    required={!editingId}
                    style={{ display: "none" }}
                  />

                  {/* Styled label as file input button */}
                  <label htmlFor="image" className="custom-file-label">
                    Choose Image
                  </label>

                  {/* Image Preview */}
                  {previewImage && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "10px",
                      }}
                    >
                      <img
                        src={previewImage}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "10px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="discountText">Discount Sticker</label>
                  <input
                    type="text"
                    id="discountText"
                    name="discountText"
                    value={formData.discountText}
                    onChange={handleChange}
                    placeholder="e.g. 50% OFF"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="secondaryImage">Secondary Photo (optional)</label>

                  <input
                    type="file"
                    id="secondaryImage"
                    name="secondaryImage"
                    onChange={handleChange}
                    accept="image/*"
                    style={{ display: "none" }}
                  />

                  <label htmlFor="secondaryImage" className="custom-file-label">
                    Choose Image
                  </label>

                  {previewSecondaryImage && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        marginTop: "10px",
                      }}
                    >
                      <img
                        src={previewSecondaryImage}
                        alt="Secondary preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          borderRadius: "10px",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                </div>

                <div className="form-buttons">
                  <button type="submit" className="submit-btn">
                    {editingId ? "Update" : "Add"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* List Table */}
        <table className="variety-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Category</th>
              <th>Route To</th>
              <th>Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {landingBanner.map((landingbanner) => (
              <tr key={landingbanner._id}>
                <td>{landingbanner.title || "—"}</td>
                <td>{formatText(landingbanner.category)}</td>
                <td>{formatText(landingbanner.routeTo)}</td>
                <td>
                  <img
                    src={`${landingbanner.image}`}
                    alt={landingbanner.collectionName}
                  />
                </td>
                <td>
                  <button onClick={() => handleEdit(landingbanner)}>
                    Edit
                  </button>
                  {isAdmin() && (
                    <button onClick={() => handleDelete(landingbanner._id)}>
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Popup */}
      {showConfirm && (
        <div className="confirm-popup">
          <div className="confirm-content">
            <p>Are you sure you want to delete this banner?</p>
            <div className="popup-actions">
              <button
                onClick={() => confirmDelete(selectedVideoId)}
                className="confirm-btn"
              >
                Confirm
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </>
  );
};

export default LandingBanner;
